import React, { ReactElement } from "react";
import ReactDOMClient from "react-dom/client";
import _ from 'lodash';
import type { IAngularStatic } from "angular";
import { IDialogService, INavigationService, ITromanContext, IUserServer, TromanContext, TromanSystem } from "./hooks/useTromanService";

declare var angular: IAngularStatic;
const angularModule = angular.module('tromanWeb');

const validSpec = /^(<|@)/;
const callbackSpec = /^&/;

type PropsBindings<TProps> = {
    [P in keyof TProps]-?:
        TProps[P] extends Function ? `<${string}` | [`&${string}`, ...string[]] : 
        TProps[P] extends string ? `<${string}` | `@${string}`
        : `<${string}`;
}

/**
 * Bindings are specified by an object similar to angular isolate scope and must match the props of the component.
 * The component is re-rendered with new props when angulars change detection updates the values.
 * 
 * Note that it is the callers responsibility to make sure that anything passed as props is not mutated.
 * It is also the callers responsibility to only supply values (including undefined) that match the props.
 * There is no runtime type checking available.
 * 
 * Example props: 
 * interface IProps {
 *   stringVal: string;
 *   textVal: string;
 *   scopeCallback: (first, second) => string;
 *   directCallback: (first, second) => string;
 * }
 * 
 * Example bindings:
 * {
 *   stringVal: '<',
 *   textVal: '@',
 *   scopeCallback: ["&", "$first", "$second"],
 *   directCallback: '<'
 * }
 * 
 * Available variants are as follows:
 * 
 * One way binding: '<'
 * <component string-val="ctrl.value"></component>
 * 
 * Attribute binding: '@'
 * <component string-val="This is my title"></component>
 * 
 * Callback evaluated in scope: ["&", "$first", "$second"]
 * Parameters are mapped in order to the names specified in the binding
 * ctrl.func = function(first, second) { return "PARS:" + first + "," + second + "," + this.value; }
 * <component scope-callback="ctrl.func($first, $second)"></component>
 * 
 * Direct callback: '<'
 * Special case of one way binding with functions.
 * Any functions provided as values are wrapped in angular $apply.
 * Note that as always in JS, "this" is not preserved => arrow syntax must be used if "this" is needed.
 * ctrl.func = (first, second) => { return "PARS:" + first + "," + second + "," + this.value; }
 * <component direct-callback="ctrl.func"></component>
 */
export function registerReactComponent<P extends {}>(Component: React.FunctionComponent<P> | React.ComponentClass<P>, name: string, reactBindings: PropsBindings<P>) {
    // Typescript should catch invalid descriptors as long as the caller of registerReactComponent is written in typescript
    let bindings = _.mapValues(reactBindings, (spec, key) => {
        if (_.isArray(spec)) {
            if (!callbackSpec.test(spec[0])) {
                throw new Error("Invalid property specification");
            }
            return spec[0];
        } else {
            if (!validSpec.test(spec)) {
                throw new Error("Invalid property specification");
            }
            return spec;
        }
    });
    
    function checkResult(result: any) {
        if (result && _.isFunction(result.then)) {
            return Promise.resolve(result);
        }
        return result;
    }
    
    //console.log('bindings.' + name, bindings);

    return angularModule.component(name, {
        bindings: bindings as any,
        controller: ['$element', '$scope', 'reactServiceImplementation', function(this: any, $element: any, $scope: any, reactServiceImplementation: any) {
            const root = ReactDOMClient.createRoot($element[0]);
            const props = Object.create(null);

            // Callback bindings need to be executed inside $apply so that angulars change detection works
            _.each(reactBindings, (v, k) => {
                if (_.isArray(v)) {
                    let localNames = _.chain(v).drop().map((name, index) => ({name, index})).keyBy(x => x.name).mapValues(x => x.index).value();
                    props[k] = (...args: any[]) => {
                        let localParameters = _.mapValues(localNames, x => args[x]);
                        //console.log('Call Wrapped.' + k, { reactParameters: args, angularLocals: localParameters });
                        return checkResult($scope.$apply(() => this[k](localParameters)));
                    };
                }
            });
            
            this.$onChanges = function(changed: any) {
                _.each(changed, (v,k) => {
                    if (_.isFunction(v.currentValue)) {
                        let fn = v.currentValue;
                        props[k] = (...args: any[]) => checkResult($scope.$apply(() => fn(...args)));
                    } else {
                        props[k] = v.currentValue;
                    }
                });

                let immutableProps = _.clone(props);
                //console.log('props.' + name, props);
                let reactElement: ReactElement = React.createElement(Component, immutableProps);
                reactElement = React.createElement(TromanContext.Provider, { value: reactServiceImplementation.tromanImplementation }, reactElement);
                //reactElement = React.createElement(React.StrictMode, undefined, reactElement);
                root.render(reactElement);
            };
            
            this.$onDestroy = () => {
                root.unmount();
            };
        }]
    });
}

angularModule.factory('reactServiceImplementation', ['dialogService', 'stateService', 'downloadService', 'tromanServer', 'tromanGlobals', 'accountServer', 'isTroman',
    function(dialogService: any, stateService: any, downloadService: any, tromanServer: any, tromanGlobals: any, accountServer: any, isTroman: any) {

        const dialogImplementation: IDialogService = {
            confirm(message, title) {
                return Promise.resolve(dialogService.confirm(message, title)).then(() => true).catch(() => false);
            },
            notify(message, title) {
                return Promise.resolve(dialogService.notify(message, title)).then(() => true).catch(() => false);
            },
            warning(message) {
                return Promise.resolve(dialogService.warning(message)).then(() => true).catch(() => false);
            },
            error(err) {
                return Promise.resolve(dialogService.error(err)).then(() => true).catch(() => false);
            },
        };

        const navigationImplementation: INavigationService = {
            guard(callback) {
                return stateService.guard(callback);
            },
            addLock(initial) {
                return stateService.addLock(initial);
            },
            navigateTo(target, ignoreLock, options) {
                if (ignoreLock) {
                    stateService.forceGo(target.destination, target.params, options);
                } else {
                    stateService.go(target.destination, target.params, options);
                }
            },
        };

        const userServerImplementation : IUserServer = {
            getAllUsers() {
                return accountServer.Users.getUsers();
            },
        };

        const tromanImplementation: ITromanContext = {
            tromanSystem: isTroman ? TromanSystem.Troman : TromanSystem.Valman,
            apiServer: tromanServer,
            dialog: dialogImplementation,
            navigation: navigationImplementation,
            getSystemInfo: () => tromanGlobals.systemInfo || undefined,
            download: downloadService,
            userServer: userServerImplementation,
        };

        return {
            tromanImplementation,
        };

    }
]);